import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { OrderCreationLayout } from '@/components/order/OrderCreationLayout'
import { 
  CheckCircle2, 
  CreditCard, 
  Building2, 
  XCircle, 
  ExternalLink, 
  AlertCircle,
  Calendar,
  Receipt,
  Loader2,
  Wallet,
  Banknote,
  Upload,
  FileCheck,
} from 'lucide-react'
import {
  getOrderDetails,
  getPaymentStrategies,
  getOrderInvoice,
  createPaymentTransaction,
  confirmBankTransferPayment,
  confirmCashPayment,
  simulatePayment,
} from '@/services/orderApi'
import { useOrderCreationStore } from '@/store/orderCreationStore'
import { useLocalization } from '@/contexts/LocalizationContext'
import { currencySymbol } from '@/utils/formatters'
import { format } from 'date-fns'
import type { Order, PaymentStrategy, SalesInvoice } from '@/services/orderApi'

export function PaymentPage() {
  const navigate = useNavigate()
  const { locale, t } = useLocalization()
  const {
    orderId,
    orderNumber: storedOrderNumber,
    clientName,
    setTransactionId,
    setInvoiceId,
    reset,
    getCreateOrderEntryPath,
  } = useOrderCreationStore()

  const [order, setOrder] = useState<Order | null>(null)
  const [invoice, setInvoice] = useState<SalesInvoice | null>(null)
  const [paymentStrategies, setPaymentStrategies] = useState<PaymentStrategy[]>([])
  const [selectedStrategy, setSelectedStrategy] = useState<PaymentStrategy | null>(null)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [paymentWindow, setPaymentWindow] = useState<Window | null>(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  // Bank transfer fields
  const [bankReferenceNumber, setBankReferenceNumber] = useState('')
  const [bankAttachment, setBankAttachment] = useState<File | null>(null)
  const bankFileInputRef = useRef<HTMLInputElement>(null)

  // Cash fields
  const [cashNotes, setCashNotes] = useState('')

  const [paymentResult, setPaymentResult] = useState<{
    success: boolean
    message: string
    transactionNumber?: string
    orderNumber?: string
    amount?: string
    currency?: string
  } | null>(null)

  useEffect(() => {
    if (!orderId) {
      navigate(getCreateOrderEntryPath(), { replace: true })
      return
    }

    loadPaymentData()
  }, [orderId, navigate, getCreateOrderEntryPath])

  useEffect(() => {
    return () => {
      if (paymentWindow) {
        paymentWindow.close()
      }
    }
  }, [paymentWindow])

  const loadPaymentData = async () => {
    if (!orderId) return

    try {
      setLoading(true)
      setError(null)

      const [orderData, strategies, invoiceData] = await Promise.all([
        getOrderDetails(orderId),
        getPaymentStrategies(),
        getOrderInvoice(orderId),
      ])

      setOrder(orderData)
      setPaymentStrategies(strategies)
      setInvoice(invoiceData)

      const defaultStrategy = strategies.find((s) => s.isDefault)
      if (defaultStrategy) {
        setSelectedStrategy(defaultStrategy)
      } else if (strategies.length > 0) {
        setSelectedStrategy(strategies[0])
      }
    } catch (err: any) {
      setError(err.message || t('orderCreation.failedToLoadPayment'))
      console.error('Error loading payment data:', err)
    } finally {
      setLoading(false)
    }
  }

  const getProcessorName = () => selectedStrategy?.processorName?.toLowerCase() || ''

  const validatePaymentFields = (): boolean => {
    if (!selectedStrategy) {
      setError(t('orderCreation.selectPaymentMethod'))
      return false
    }

    const processor = getProcessorName()

    if (processor === 'bank_transfer' || processor === 'banktransfer') {
      if (!bankReferenceNumber.trim()) {
        setError(t('orderCreation.enterBankReference'))
        return false
      }
      if (!bankAttachment) {
        setError(t('orderCreation.uploadPaymentReceipt'))
        return false
      }
    }

    return true
  }

  const handlePaymentClick = () => {
    if (!validatePaymentFields()) return
    setShowConfirmDialog(true)
  }

  const handleConfirmPayment = async () => {
    setShowConfirmDialog(false)
    await processPayment()
  }

  const getRawGraphqlId = (id: string) => {
    try {
      const decoded = atob(id)
      const separatorIndex = decoded.indexOf(':')
      return separatorIndex >= 0 ? decoded.slice(separatorIndex + 1) : id
    } catch {
      return id
    }
  }

  const processPayment = async () => {
    if (!selectedStrategy || !invoice || !orderId) {
      setError(t('orderCreation.selectPaymentMethod'))
      return
    }

    try {
      setProcessing(true)
      setError(null)

      // Step 1: Create the payment transaction (pending)
      const transactionResponse = await createPaymentTransaction({
        sales_invoice_id: getRawGraphqlId(invoice.id),
        acceptance_strategy_id: getRawGraphqlId(selectedStrategy.id),
      })

      const { transaction, payment_result } = transactionResponse
      const processor = getProcessorName()

      // Step 2: Handle based on payment method
      if (processor === 'paymob') {
        // Paymob: redirect to payment gateway
        if (payment_result.requires_redirect && payment_result.payment_url) {
          const windowFeatures = 'width=800,height=600,scrollbars=yes,resizable=yes'
          const paymentWin = window.open(
            payment_result.payment_url,
            'Payment',
            windowFeatures
          )

          if (!paymentWin) {
            setError('Please allow popups to complete payment')
            return
          }

          setPaymentWindow(paymentWin)

          const checkInterval = setInterval(async () => {
            if (paymentWin.closed) {
              clearInterval(checkInterval)
              try {
                const updatedOrder = await getOrderDetails(orderId)
                if (updatedOrder.paymentStatus === 'paid' || updatedOrder.hasPayments) {
                  handlePaymentSuccess(transaction)
                } else {
                  handlePaymentFailure('Payment was not completed', transaction)
                }
              } catch {
                handlePaymentFailure(
                  'Payment status could not be verified. Please check your order status.',
                  transaction
                )
              }
            }
          }, 1000)
        } else if (payment_result.success) {
          handlePaymentSuccess(transaction)
        } else {
          handlePaymentFailure(
            payment_result.error_message || payment_result.message || 'Payment failed',
            transaction
          )
        }
      } else if (processor === 'bank_transfer' || processor === 'banktransfer') {
        // Bank Transfer: confirm with reference number and attachment
        try {
          const confirmResult = await confirmBankTransferPayment({
            transaction_id: transaction.id,
            reference_number: bankReferenceNumber.trim(),
            attachment: bankAttachment!,
          })

          if (confirmResult.success) {
            handlePaymentSuccess({
              ...transaction,
              transaction_number: confirmResult.transaction.transaction_number,
              status: confirmResult.transaction.status,
            })
          } else {
            handlePaymentFailure('Bank transfer confirmation failed', transaction)
          }
        } catch (confirmErr: any) {
          handlePaymentFailure(
            confirmErr.response?.data?.error || confirmErr.message || 'Failed to confirm bank transfer',
            transaction
          )
        }
      } else if (processor === 'cash') {
        // Cash: confirm with optional notes
        try {
          const confirmResult = await confirmCashPayment({
            transaction_id: transaction.id,
            notes: cashNotes.trim() || undefined,
          })

          if (confirmResult.success) {
            handlePaymentSuccess({
              ...transaction,
              transaction_number: confirmResult.transaction.transaction_number,
              status: confirmResult.transaction.status,
            })
          } else {
            handlePaymentFailure('Cash payment confirmation failed', transaction)
          }
        } catch (confirmErr: any) {
          handlePaymentFailure(
            confirmErr.response?.data?.error || confirmErr.message || 'Failed to confirm cash payment',
            transaction
          )
        }
      } else {
        // Unknown processor — treat like the generic flow
        if (payment_result.success) {
          handlePaymentSuccess(transaction)
        } else {
          handlePaymentFailure(
            payment_result.error_message || payment_result.message || 'Payment failed',
            transaction
          )
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to process payment'
      setError(errorMessage)
      console.error('Error processing payment:', err)
      handlePaymentFailure(errorMessage)
    } finally {
      setProcessing(false)
    }
  }

  const handleSimulatePayment = async () => {
    if (!invoice || !orderId) return

    try {
      setProcessing(true)
      setError(null)

      const transactionResponse = await createPaymentTransaction({
        sales_invoice_id: getRawGraphqlId(invoice.id),
        acceptance_strategy_id: getRawGraphqlId(selectedStrategy?.id || paymentStrategies[0]?.id || ''),
      })

      if (transactionResponse.transaction?.id) {
        const result = await simulatePayment(transactionResponse.transaction.id)
        if (result.success) {
          handlePaymentSuccess({
            ...transactionResponse.transaction,
            transaction_number: result.transaction.transaction_number,
          })
        } else {
          handlePaymentFailure(
            result.message || 'Payment simulation failed',
            transactionResponse.transaction
          )
        }
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to simulate payment'
      setError(errorMessage)
      console.error('Error simulating payment:', err)
      handlePaymentFailure(errorMessage)
    } finally {
      setProcessing(false)
    }
  }

  const handlePaymentSuccess = (transaction?: {
    id: string
    transaction_number?: string
    amount?: string
    currency?: string
    status?: string
  }) => {
    setPaymentResult({
      success: true,
      message: t('orderCreation.paymentSuccessful'),
      transactionNumber: transaction?.transaction_number,
      orderNumber: order?.orderNumber,
      amount: transaction?.amount || invoice?.totalAmount,
      currency: transaction?.currency || invoice?.currency,
    })
  }

  const handlePaymentFailure = (
    errorMessage: string,
    transaction?: {
      id: string
      transaction_number?: string
      amount?: string
      currency?: string
      status?: string
    }
  ) => {
    setPaymentResult({
      success: false,
      message: errorMessage,
      transactionNumber: transaction?.transaction_number,
      orderNumber: order?.orderNumber,
      amount: transaction?.amount || invoice?.totalAmount,
      currency: transaction?.currency || invoice?.currency,
    })
  }

  const handleClosePaymentResult = () => {
    const next = getCreateOrderEntryPath()
    reset()
    navigate(next)
  }

  const handleViewOrderDetails = () => {
    navigate(`/orders?orderId=${orderId}`)
  }

  const handleTryPaymentAgain = () => {
    setPaymentResult(null)
    setError(null)
    setBankReferenceNumber('')
    setBankAttachment(null)
    setCashNotes('')
    if (bankFileInputRef.current) {
      bankFileInputRef.current.value = ''
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setBankAttachment(e.target.files[0])
      setError(null)
    }
  }

  const getPaymentIcon = (processorName: string) => {
    switch (processorName.toLowerCase()) {
      case 'paymob':
        return <CreditCard className="h-5 w-5" />
      case 'bank_transfer':
      case 'banktransfer':
        return <Building2 className="h-5 w-5" />
      case 'cash':
        return <Banknote className="h-5 w-5" />
      default:
        return <Wallet className="h-5 w-5" />
    }
  }

  const getPayButtonLabel = () => {
    const processor = getProcessorName()
    if (processor === 'bank_transfer' || processor === 'banktransfer') {
      return t('orderCreation.bankTransferSubmit')
    }
    if (processor === 'cash') {
      return t('orderCreation.confirmCashPayment')
    }
    if (processor === 'paymob') {
      return t('orderCreation.payWithPaymob')
    }
    return t('orderCreation.payNow')
  }

  const renderPaymentMethodFields = () => {
    if (!selectedStrategy) return null
    const processor = getProcessorName()

    if (processor === 'bank_transfer' || processor === 'banktransfer') {
      return (
        <Card className="border-dashed">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              {t('orderCreation.bankTransferDetails')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>
                {t('orderCreation.referenceNumber')}
                <span className="text-destructive ms-1">*</span>
              </Label>
              <Input
                placeholder={t('orderCreation.referenceNumberPlaceholder')}
                value={bankReferenceNumber}
                onChange={(e) => {
                  setBankReferenceNumber(e.target.value)
                  setError(null)
                }}
              />
            </div>

            <div className="space-y-2">
              <Label>
                {t('orderCreation.paymentReceipt')}
                <span className="text-destructive ms-1">*</span>
              </Label>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  bankAttachment
                    ? 'border-primary bg-primary/5'
                    : 'border-muted-foreground/25 hover:border-primary/50'
                }`}
                onClick={() => bankFileInputRef.current?.click()}
              >
                <input
                  ref={bankFileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {bankAttachment ? (
                  <div className="flex items-center justify-center gap-2 text-primary">
                    <FileCheck className="h-5 w-5" />
                    <span className="font-medium">{bankAttachment.name}</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {t('orderCreation.uploadPaymentReceiptHint')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )
    }

    if (processor === 'cash') {
      return (
        <Card className="border-dashed">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Banknote className="h-4 w-4" />
              {t('orderCreation.cashPaymentDetails')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>
                {t('orderCreation.notesOptionalLabel')}
              </Label>
              <Textarea
                placeholder={t('orderCreation.cashNotesPlaceholder')}
                rows={3}
                value={cashNotes}
                onChange={(e) => setCashNotes(e.target.value)}
              />
            </div>
            {/* <p className="text-sm text-muted-foreground">
              {t('orderCreation.cashPaymentConfirmMessage')}
            </p> */}
          </CardContent>
        </Card>
      )
    }

    if (processor === 'paymob') {
      return (
        <Card className="border-dashed">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-muted-foreground">
              <CreditCard className="h-5 w-5" />
              <p className="text-sm">
                {t('orderCreation.paymobRedirectMessage')}
              </p>
            </div>
          </CardContent>
        </Card>
      )
    }

    return null
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-1/3" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-20 w-full" />
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-1/2" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )
    }

    if (paymentResult) {
      return (
        <Card
          className={
            paymentResult.success
              ? 'border-primary bg-primary/5 max-w-2xl mx-auto'
              : 'border-destructive bg-destructive/5 max-w-2xl mx-auto'
          }
        >
          <CardHeader>
            <div
              className={`flex items-center gap-2 ${
                paymentResult.success ? 'text-primary' : 'text-destructive'
              }`}
            >
              {paymentResult.success ? (
                <CheckCircle2 className="h-8 w-8" />
              ) : (
                <XCircle className="h-8 w-8" />
              )}
              <CardTitle className="text-2xl">
                {paymentResult.success 
                  ? t('orderCreation.paymentSuccessful')
                  : t('orderCreation.paymentFailed')}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <p
              className={
                paymentResult.success
                  ? 'text-primary/80'
                  : 'text-destructive/80'
              }
            >
              {paymentResult.message}
            </p>

            <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
              {paymentResult.orderNumber && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t('orderCreation.orderNumber')}:
                  </span>
                  <span className="font-semibold">{paymentResult.orderNumber}</span>
                </div>
              )}
              {paymentResult.transactionNumber && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t('orderCreation.transactionNumber')}:
                  </span>
                  <span className="font-semibold">{paymentResult.transactionNumber}</span>
                </div>
              )}
              {paymentResult.amount && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t('common.amount')}:
                  </span>
                  <span className="font-semibold">
                    {paymentResult.amount} {currencySymbol(paymentResult.currency)}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-3 pt-4">
              {!paymentResult.success && (
                <>
                  <Button
                    onClick={handleTryPaymentAgain}
                    variant="default"
                    className="w-full"
                  >
                    <CreditCard className="h-4 w-4 me-2" />
                    {t('orderCreation.tryPaymentAgain')}
                  </Button>
                  <Button
                    onClick={handleViewOrderDetails}
                    variant="outline"
                    className="w-full"
                  >
                    <ExternalLink className="h-4 w-4 me-2" />
                    {t('orderCreation.viewOrderDetailsRetry')}
                  </Button>
                </>
              )}
              <Button
                onClick={handleClosePaymentResult}
                variant={paymentResult.success ? 'default' : 'outline'}
                className="w-full"
              >
                {paymentResult.success 
                  ? t('orderCreation.createNewOrder')
                  : t('orderCreation.backToOrderCreation')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )
    }

    if (error && !loading) {
      return (
        <Card className="border-destructive max-w-2xl mx-auto">
          <CardHeader>
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <CardTitle>
                {t('common.error')}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={loadPaymentData}>
              {t('common.retry')}
            </Button>
          </CardContent>
        </Card>
      )
    }

    return (
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Order Summary & Invoice */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Summary */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-primary" />
                <CardTitle>
                  {t('orderCreation.orderSummary')}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {order && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t('orderCreation.orderNumber')}
                      </p>
                      <p className="font-semibold text-lg">{order.orderNumber}</p>
                    </div>
                    <Badge variant="secondary">{order.status}</Badge>
                  </div>
                  
                  {order.beneficiaryName && (
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        {t('orderCreation.beneficiary')}
                      </p>
                      <p className="font-semibold">{order.beneficiaryName}</p>
                    </div>
                  )}
                  
                  {order.appointments && order.appointments.length > 0 && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {t('orderCreation.appointments')}
                      </p>
                      <div className="space-y-2">
                        {order.appointments.map((apt, idx) => (
                          <div
                            key={apt.id}
                            className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                          >
                            <div>
                              <p className="font-medium">
                                {t('orderCreation.appointmentNumber', { number: idx + 1 })}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {format(new Date(apt.scheduledStart), 'PPP p')}
                              </p>
                            </div>
                            <Badge variant="outline">{apt.status}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Invoice Details */}
          {invoice && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>
                  {t('orderCreation.invoiceDetails')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        {t('orderCreation.invoiceNumber')}
                      </p>
                    <p className="font-semibold">{invoice.invoiceNumber}</p>
                  </div>
                  <div className="border-t pt-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {t('orderCreation.subtotal')}
                      </span>
                      <span className="font-semibold">
                        {invoice.subtotal} {currencySymbol(invoice.currency)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {t('orderCreation.tax')}
                      </span>
                      <span className="font-semibold">
                        {invoice.taxAmount} {currencySymbol(invoice.currency)}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-3">
                      <span>{t('common.total')}</span>
                      <span className="text-primary">
                        {invoice.totalAmount} {currencySymbol(invoice.currency)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment Method Specific Fields */}
          {renderPaymentMethodFields()}
        </div>

        {/* Payment Methods Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>
                {t('orderCreation.paymentMethod')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {paymentStrategies.map((strategy) => (
                  <Card
                    key={strategy.id}
                    className={`cursor-pointer transition-all ${
                      selectedStrategy?.id === strategy.id
                        ? 'border-primary shadow-md ring-2 ring-primary/20'
                        : 'hover:shadow-md hover:border-primary/50'
                    }`}
                    onClick={() => {
                      setSelectedStrategy(strategy)
                      setError(null)
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                          {getPaymentIcon(strategy.processorName)}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{strategy.name}</p>
                          {strategy.description && (
                            <p className="text-xs text-muted-foreground">
                              {strategy.description}
                            </p>
                          )}
                        </div>
                        {selectedStrategy?.id === strategy.id ? (
                          <CheckCircle2 className="h-5 w-5 text-primary" />
                        ) : strategy.isDefault ? (
                          <Badge variant="secondary" className="text-xs">
                            {t('orderCreation.recommended')}
                          </Badge>
                        ) : null}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Pay Button Card */}
          <Card className="bg-muted/30">
            <CardContent className="p-6">
              <div className="space-y-4">
                {invoice && (
                  <div className="text-center p-4 bg-background rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">
                      {t('orderCreation.totalAmount')}
                    </p>
                    <p className="text-3xl font-bold text-primary">
                      {invoice.totalAmount}
                      <span className="text-lg ms-1">{currencySymbol(invoice.currency)}</span>
                    </p>
                  </div>
                )}

                {error && (
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <p className="text-sm text-destructive">{error}</p>
                  </div>
                )}

                <Button
                  className="w-full h-12 text-lg"
                  onClick={handlePaymentClick}
                  disabled={!selectedStrategy || processing}
                >
                  {processing ? (
                    <>
                      <Loader2 className="me-2 h-5 w-5 animate-spin" />
                      {t('common.processing')}
                    </>
                  ) : (
                    getPayButtonLabel()
                  )}
                </Button>
                {/* {import.meta.env.DEV && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleSimulatePayment}
                    disabled={processing}
                  >
                    {t('orderCreation.simulatePayment')}
                  </Button>
                )} */}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <OrderCreationLayout
      titleKey="orderCreation.completePayment"
      descriptionKey="orderCreation.completePaymentDescription"
      step={7}
      backPath="/create-order/questions"
    >
      <div className="max-w-5xl mx-auto">
        {/* Order Info Header */}
        {!paymentResult && !loading && (
          <Card className="mb-6 bg-muted/30">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Receipt className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">
                    {t('orderCreation.paymentFor')}
                  </p>
                  <p className="font-semibold">
                    {clientName} - {storedOrderNumber || order?.orderNumber}
                  </p>
                </div>
                <Badge variant="secondary">
                  {t('orderCreation.stepOf', { step: 7, total: 7 })}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {renderContent()}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('orderCreation.confirmPayment')}
            </DialogTitle>
            <DialogDescription>
              {t('orderCreation.confirmPaymentDescription', {
                method: selectedStrategy?.name ?? '',
                amount: invoice?.totalAmount ?? '',
                currency: currencySymbol(invoice?.currency ?? ''),
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleConfirmPayment}>
              {t('orderCreation.confirmPaymentButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </OrderCreationLayout>
  )
}
